import { useCallback, useEffect, useState } from "react";
import {
  deleteMyAvatar,
  listMyAvatars,
  saveMyAvatar,
} from "@/services/ads/videos/videoDraftService";
import type {
  SavedAvatar,
  SaveAvatarRequest,
} from "@/services/ads/videos/videoDraft.types";
import type { UGCAvatarConfig } from "../types/videoStudio.types";

// Phase 2 V2 — Fase C. Session-scoped cache of the user's saved avatars.
// Same pattern as useCuratedVoices: module-level cache keeps the wizard
// snappy across open/close cycles inside a single session. Save/delete
// mutate the cache optimistically so the UI reflects the change before
// the server round-trip finishes.
let cachedAvatars: SavedAvatar[] | null = null;
let inFlight: Promise<SavedAvatar[] | null> | null = null;

async function fetchOnce(): Promise<SavedAvatar[] | null> {
  if (cachedAvatars) return cachedAvatars;
  if (inFlight) return inFlight;
  inFlight = listMyAvatars().then(({ res }) => {
    cachedAvatars = res ?? [];
    inFlight = null;
    return cachedAvatars;
  });
  return inFlight;
}

export interface ParsedSavedAvatar extends SavedAvatar {
  parsedConfig: Partial<UGCAvatarConfig>;
}

function parseRow(row: SavedAvatar): ParsedSavedAvatar {
  let parsed: Partial<UGCAvatarConfig> = {};
  try {
    parsed = JSON.parse(row.avatar_config) as Partial<UGCAvatarConfig>;
  } catch {
    parsed = {};
  }
  return { ...row, parsedConfig: parsed };
}

export interface UseMyAvatarsResult {
  avatars: ParsedSavedAvatar[];
  isLoading: boolean;
  error: boolean;
  save: (payload: SaveAvatarRequest) => Promise<ParsedSavedAvatar | null>;
  remove: (id: number) => Promise<boolean>;
  refresh: () => void;
}

export function useMyAvatars(enabled: boolean = true): UseMyAvatarsResult {
  const [avatars, setAvatars] = useState<ParsedSavedAvatar[]>(
    cachedAvatars ? cachedAvatars.map(parseRow) : []
  );
  const [isLoading, setIsLoading] = useState(!cachedAvatars && enabled);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    const result = await fetchOnce();
    if (result) {
      setAvatars(result.map(parseRow));
    } else {
      setError(true);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (cachedAvatars) {
      setAvatars(cachedAvatars.map(parseRow));
      setIsLoading(false);
      return;
    }
    load();
  }, [enabled, load]);

  const save = useCallback(
    async (payload: SaveAvatarRequest): Promise<ParsedSavedAvatar | null> => {
      const { res, error: apiError } = await saveMyAvatar(payload);
      if (apiError || !res) return null;
      cachedAvatars = [res, ...(cachedAvatars ?? [])];
      const parsed = parseRow(res);
      setAvatars(cachedAvatars.map(parseRow));
      return parsed;
    },
    []
  );

  const remove = useCallback(async (id: number): Promise<boolean> => {
    const { res, error: apiError } = await deleteMyAvatar(id);
    if (apiError || !res?.deleted) return false;
    cachedAvatars = (cachedAvatars ?? []).filter((a) => a.id !== id);
    setAvatars(cachedAvatars.map(parseRow));
    return true;
  }, []);

  const refresh = useCallback(() => {
    cachedAvatars = null;
    inFlight = null;
    load();
  }, [load]);

  return { avatars, isLoading, error, save, remove, refresh };
}
